import { useState, useCallback } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@/lib/utils';
import type { DdqSessionQuestion } from '@/types/api.types';
import { TierBadge } from './TierBadge';
import { SourceBadge } from './SourceBadge';
import { CitationPanel } from './CitationPanel';
import { ActionButtons } from './ActionButtons';
import { ResponseEditor } from './ResponseEditor';
import { VaultCapturePrompt } from './VaultCapturePrompt';
import { ProvenanceTrace } from './ProvenanceTrace';
import { getTierColors } from '../utils/tierColors';

export interface EvidencePaneProps {
  question: DdqSessionQuestion | null;
  sessionId: string | null;
  isEditing: boolean;
  editingResponse: string;
  onAccept: () => void;
  onReject: () => void;
  onStartEdit: () => void;
  onSaveEdit: () => void;
  onCancelEdit: () => void;
  onUpdateEditingResponse: (response: string) => void;
  className?: string;
}

/**
 * The right-hand evidence pane: the active question's full detail. Unlike the
 * legacy expand/collapse card, the answer, its provenance, and the
 * Accept / Edit / Reject actions are ALWAYS visible — the reviewer never has to
 * expand a row to act. Reuses the existing review primitives.
 */
export function EvidencePane({
  question,
  sessionId,
  isEditing,
  editingResponse,
  onAccept,
  onReject,
  onStartEdit,
  onSaveEdit,
  onCancelEdit,
  onUpdateEditingResponse,
  className,
}: EvidencePaneProps) {
  const [vaultCaptured, setVaultCaptured] = useState(false);

  const handleVaultDone = useCallback(() => setVaultCaptured(true), []);

  if (!question) {
    return (
      <div
        className={cn(
          'border border-border rounded-lg p-8 text-center text-sm text-muted-foreground',
          className,
        )}
      >
        Select a question from the queue to review its answer.
      </div>
    );
  }

  const tier = getTierColors(question.response_tier);
  const userAction = question.userAction || 'Pending';
  const rawResponseText = question.userResponse || question.suggested_response;
  const responseText = rawResponseText?.replace(/^Response:\s*/i, '');

  const showVaultCapture =
    userAction === 'Accepted' &&
    question.answer_source !== 'vault_data' &&
    question.answer_source !== 'firm_required' &&
    !vaultCaptured;

  return (
    <div className={cn('border-l-4 border border-border rounded-lg p-5 space-y-4', tier.border, className)}>
      {/* Question header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-muted-foreground">Q{question.index + 1}</span>
          <TierBadge tier={question.response_tier} size="sm" />
          <SourceBadge source={question.answer_source} size="sm" />
          {userAction !== 'Pending' && (
            <span
              className={cn(
                'text-xs font-medium px-2 py-0.5 rounded',
                userAction === 'Accepted' && 'bg-green-100 text-green-700 dark:bg-green-950/40 dark:text-green-300',
                userAction === 'Modified' && 'bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300',
                userAction === 'Rejected' && 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300',
              )}
            >
              {userAction}
            </span>
          )}
        </div>
        <p className="text-base font-semibold text-foreground leading-relaxed">
          {question.incoming_question}
        </p>
      </div>

      {/* Answer */}
      <div className="space-y-2">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Answer
        </label>
        {isEditing ? (
          <ResponseEditor
            value={editingResponse}
            originalResponse={question.suggested_response}
            onChange={onUpdateEditingResponse}
            onSave={onSaveEdit}
            onCancel={onCancelEdit}
          />
        ) : responseText ? (
          <div className="bg-muted/50 rounded-lg p-4 text-sm leading-relaxed prose prose-sm prose-neutral dark:prose-invert prose-table:border-collapse prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-1.5 prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-1.5 prose-th:bg-muted">
            <Markdown remarkPlugins={[remarkGfm]}>{responseText}</Markdown>
          </div>
        ) : (
          <p className="text-sm text-tier-nomatch">
            No answer yet — edit to provide one.
          </p>
        )}
      </div>

      {/* Provenance — promoted to a first-class element */}
      {!isEditing && <ProvenanceTrace question={question} />}

      {/* Citations */}
      {!isEditing && question.citations.length > 0 && (
        <CitationPanel citations={question.citations} />
      )}

      {/* Persistent action bar */}
      {!isEditing && (
        <div className="pt-3 border-t border-border space-y-2">
          <ActionButtons
            onAccept={onAccept}
            onModify={onStartEdit}
            onReject={onReject}
            currentAction={userAction}
          />
          <p className="text-[11px] text-muted-foreground">
            <span className="font-mono">↑↓</span> move · <span className="font-mono">A</span> accept &amp; next ·{' '}
            <span className="font-mono">E</span> edit · <span className="font-mono">R</span> reject ·{' '}
            <span className="font-mono">S</span> skip
          </p>
        </div>
      )}

      {/* Vault capture flywheel */}
      {!isEditing && showVaultCapture && sessionId && (
        <VaultCapturePrompt
          questionText={question.incoming_question}
          answerText={question.userResponse || question.suggested_response}
          category={question.category}
          sessionId={sessionId}
          responseId={question.question_id}
          onSave={handleVaultDone}
          onSkip={handleVaultDone}
        />
      )}
    </div>
  );
}
